import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, concat, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  tap,
  switchMap,
  catchError,
  map,
  takeUntil,
} from 'rxjs/operators';
import Swal from 'sweetalert2';

import { DefaultPickingLocation } from '@app/core/_constant/location.constants';
import { LocationService } from '@app/core/locations/services/location.service';
import { PickingLocation } from '@app/core/locations/store/locations.model';
import { PoService } from '@app/core/purchase-orders/services/po.service';
import { RfqService } from '@app/core/quotes';
import { RfqLotService } from '@app/core/rfq-lots/services/rfq-lots.service';
import { SettingsService } from '@app/core/settings/services/settings.service';
import { environment } from '@app/env-service';
import { RfqLotFormComponent } from '@app/modules/purchase-orders/rfq-lot-form/rfq-lot-form.component';

@Component({
  selector: 'cnc-defective-reports-form-rfqs',
  templateUrl: './defective-reports-form-rfqs.component.html',
  styleUrls: ['./defective-reports-form-rfqs.component.scss'],
})
export class DefectiveReportsFormRfqsComponent implements OnInit, OnDestroy {
  url :string = environment.url;

  loading :boolean = false;

  po_number :any[] = [];
  pageTitle :string = 'Add Defective Report Items/SKUs';

  rrItems :any[] = [];

  purchaseOrdersList :any[] = [];
  selectedPurchaseOrders :any[] = [];

  purchaseOrder$ :Observable<any[]>;
  purchaseOrderLoading = false;
  purchaseOrderInput$ = new Subject<string>();

  gettingPurchaseOrders :boolean;

  rfq$ :Observable<any[]>;
  rfqLoading = false;
  rfqInput$ = new Subject<string>();
  rfqProduct :any;
  selectedPO :any;
  rfqPurchaseOrders :any[] = [];
  defectiveQty :number = 1;
  defective_uom_id :any = null;
  customPo :any = { quantity: 0, unitPrice: 0, purchase_order_id: 0 };

  warehouses :any[] = [];
  locations :any[] = [];
  warehouse_id :any = 0;
  location_id :any = 0;
  lot_id :number = 0;
  unitOfMeasures :any[] = [];

  locations$ :Observable<any>;
  loadingLocation$ :Observable<boolean>;
  rfqLots$ :Observable<any>;
  loadingRfqLot$ :Observable<boolean>;
  rfqLots :any[] = [];

  @Input()
  ftLocationBins :boolean;

  @Input()
  ftLotTracking :boolean;

  private onDestroy$ = new Subject();
  pickingWarehouseId :number = 0;
  inventorySettings$ :Observable<any>;
  inventorySettings :any = null;

  constructor(
    public activeModal :NgbActiveModal,
    private rfqSvc :RfqService,
    public locationSvc :LocationService,
    private poSvc :PoService,
    private rfqLotSvc :RfqLotService,
    private modalSvc :NgbModal,
    private settingsSvc :SettingsService,
  ) {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnInit() {
    this.loadpurchaseOrder();
    this.loadRfqs();
    this.getLocations();
    this.loadDataFromService();
    this.initPickingWarehouseId().subscribe();
    if (this.ftLocationBins) {
      this.initPickingLocationId().subscribe();
    }
  }

  getLocations() {
    this.locationSvc.getAll({ all: 1, parent_id: 0 }).subscribe((resp) => {
      this.warehouses = resp.filter((row) => row.parent_id == 0);
      this.locations = resp.filter((row) => row.parent_id > 0);
    });
  }

  private loadDataFromService() :void {
    this.locations$ = this.locationSvc.locationsRecord$;
    this.loadingLocation$ = this.locationSvc.loading$;

    this.rfqLots$ = this.rfqLotSvc.rfqLots$;
    this.loadingRfqLot$ = this.rfqLotSvc.loading$;
    this.getRfqLot().subscribe();
    this.inventorySettings$ = this.settingsSvc.inventorySettings$;
  }

  private getRfqLot() :Observable<any> {
    return this.rfqLots$.pipe(
      tap((response :any) => {
        this.rfqLots = response;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  onWarehouseChange(warehouseId :number) :void {
    this.loadLocationFromWarehouse(warehouseId).subscribe();
  }

  private loadLocationFromWarehouse(warehouseId :number) :Observable<any> {
    return this.locationSvc
      .getAllLocationsWithSelectedColumns(
        ['id', 'location_name', 'is_pick_location'],
        warehouseId,
        this.rfqProduct?.id,
      )
      .pipe(takeUntil(this.onDestroy$));
  }

  private loadRfqLot(rfqId :number) :Observable<any> {
    return this.rfqLotSvc
      .getByRfqIds({
        rfqIds: [rfqId],
        items: 1000,
      })
      .pipe(takeUntil(this.onDestroy$));
  }

  get enabledBinLocationAndLotTracking() :boolean {
    return (
      this.rfqProduct?.lot_tracking === 1 &&
      this.ftLocationBins &&
      this.ftLotTracking
    );
  }

  get manufacturerCode() :string {
    return this.selectedPO?.purchase_order_rfq?.purchase_order?.factory
      ?.factory_name
      ? this.selectedPO?.purchase_order_rfq?.purchase_order?.factory?.factory_name
          .replace(/\s/g, '')
          .toUpperCase()
          .substring(0, 3)
      : '';
  }

  createNewLot() :void {
    const modalRef = this.modalSvc.open(RfqLotFormComponent, {
      size: 'lg',
      windowClass: 'po-rr-modal',
    });
    if (!this.selectedPO) {
      if (Object.keys(this.customPo).length < 5) {
        Swal.fire(
          'Error',
          'Please select a purchase order or set purchase order details.',
          'error',
        );

        return;
      }
    }

    modalRef.componentInstance.rfqId = this.rfqProduct?.id;
    modalRef.componentInstance.quantityOriginal = this.selectedPO?.quantity;
    modalRef.componentInstance.manufacturerCode = this.manufacturerCode;

    modalRef.result.then(
      (data) => {
        this.loadRfqLot(this.rfqProduct?.id).subscribe();
        this.lot_id = data.id;
      },
      (_) => {},
    );
  }

  getPurchaseOrders() {
    this.gettingPurchaseOrders = true;
    this.selectedPO = null;
    this.rfqPurchaseOrders = [];
    this.defective_uom_id = null;

    this.location_id = null;
    this.loadLocationFromWarehouse(this.warehouse_id).subscribe();

    this.unitOfMeasures =
      this.rfqProduct.unit_of_measures.length > 0
        ? this.rfqProduct.unit_of_measures
        : this.rfqProduct.parent.unit_of_measures;

    this.rfqSvc.getRfqPurchaseOrders(this.rfqProduct?.id).subscribe(
      (resp :any) => {
        this.gettingPurchaseOrders = false;
        const ids = resp.map((rfqPo) => rfqPo.purchase_order_id);
        this.rfqPurchaseOrders = resp.filter(
          (rfqPo, index) => !ids.includes(rfqPo.purchase_order_id, index + 1),
        );
        this.rfqPurchaseOrders.sort((prev, next) => {
          return prev.purchase_order_id - next.purchase_order_id;
        });
      },
      (_) => {
        this.gettingPurchaseOrders = false;
      },
    );
    this.loadRfqLot(this.rfqProduct.id).subscribe();
  }

  getPOItems() {
    const selected :any = [];
    this.po_number.forEach((po) => {
      let purchaseOrder :any = this.selectedPurchaseOrders.find(
        (row) => row.id == po,
      );
      if (!purchaseOrder) {
        purchaseOrder = this.purchaseOrdersList.find((row) => row.id == po);
        selected.push(purchaseOrder);
      }
    });

    this.selectedPurchaseOrders = selected;
    selected.forEach((po) => {
      po.purchase_order_items.forEach((item) => {
        const rrItem :any = this.rrItems.find((i) => i.id == item.id);
        if (!rrItem) {
          if (this.warehouses.length > 0) {
            item.warehouse_id = this.warehouses[0].id;
            item.location_id = this.locations[0].id;
          }
          item.purchase_order_rfq_id = item.id;
          delete item.id;
          this.rrItems.push(item);
        }
      });
    });
  }

  private loadpurchaseOrder() {
    this.purchaseOrderLoading = true;
    this.purchaseOrder$ = concat(
      this.poSvc.getByPONumber('').pipe(
        tap((resp) => {
          this.purchaseOrderLoading = false;
          this.purchaseOrdersList = resp.data;
        }),
        map((resp) => {
          return resp.data;
        }),
      ), // default items
      this.purchaseOrderInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.purchaseOrderLoading = true)),
        switchMap((term) =>
          this.poSvc.getByPONumber(term).pipe(
            catchError(() => of([])), // empty list on error
            // tap(() => (this.purchaseOrderLoading = false))
            tap((resp) => {
              this.purchaseOrderLoading = false;
              this.purchaseOrdersList = resp.data;
            }),
            map((resp) => {
              return resp.data;
            }),
          ),
        ),
      ),
    );
  }

  addItems() {
    if (!this.rfqProduct) {
      Swal.fire('Error', 'Please specify product sku.', 'error');

      return;
    }
    if (!this.selectedPO) {
      if (Object.keys(this.customPo).length < 5) {
        Swal.fire(
          'Error',
          'Please select a purchase order or set purchase order details.',
          'error',
        );

        return;
      }
    }
    if (!this.defective_uom_id) {
      Swal.fire('Error', 'Defective Unit of Measure is required.', 'error');

      return;
    }
    if (!this.defectiveQty) {
      Swal.fire('Error', 'Defective quantity is required.', 'error');

      return;
    }
    if (!this.warehouse_id) {
      Swal.fire('Error', 'Please specify warehouse location of the SKU.', 'error');

      return;
    }

    const rfqId = this.selectedPO?.purchase_order_rfq?.rfq_id
      ? this.selectedPO.purchase_order_rfq.rfq_id
      : this.selectedPO?.rfq?.id
        ? this.selectedPO?.rfq?.id
        : 0;

    // this.activeModal.close(this.rrItems);
    const item :any = {
      purchase_order_id: this.selectedPO
        ? this.selectedPO.purchase_order_id
        : this.customPo.purchase_order_id,
      purchase_order_rfq_id: this.selectedPO
        ? this.selectedPO.purchase_order_rfq_id
        : 0,
      quantity: this.defectiveQty,
      unit: this.selectedPO ? this.selectedPO.unit : 'pcs',
      unit_price: this.selectedPO
        ? this.selectedPO.unit_price
        : this.customPo.unitPrice,
      line_total:
        this.defectiveQty *
        (this.selectedPO ? this.selectedPO.unit_price : this.customPo.unitPrice),
      factory_name: this.selectedPO
        ? this.selectedPO.purchase_order_rfq.purchase_order.factory.factory_name
        : this.customPo.factory_name,
      factory_id: this.selectedPO
        ? this.selectedPO.purchase_order_rfq.purchase_order.factory.id
        : 0,
      sku: this.selectedPO
        ? this.selectedPO.purchase_order_rfq.internal_sku
        : this.rfqProduct.internal_sku,
      product_name: this.selectedPO
        ? this.selectedPO.purchase_order_rfq.product_name
        : this.rfqProduct.product_name,
      received_date: this.selectedPO
        ? this.selectedPO.receiving_report.received_date
        : this.customPo.received_date,
      receiving_report_id: this.selectedPO ? this.selectedPO.id : 0,
      warehouse_id: Number(this.warehouse_id),
      location_id: this.location_id || 0,
      rfq_id: rfqId,
      unit_of_measures: this.unitOfMeasures,
      defective_uom_id: this.defective_uom_id,
      lot_id: this.lot_id ?? 0,
      lot_tracking: this.selectedPO?.purchase_order_rfq?.rfq?.lot_tracking ?? 0,
    };

    this.activeModal.close(item);
  }

  computeLineTotal(idx) {
    this.rrItems[idx].line_total =
      this.rrItems[idx].quantity * this.rrItems[idx].unit_price;
  }

  trackByFn(item :any) {
    return item.id;
  }

  private loadRfqs() {
    this.rfq$ = concat(
      of([]), // default items
      this.rfqInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.rfqLoading = true)),
        switchMap((term) =>
          this.rfqSvc.getAsync({ sku: term, physical_stock_only: true }).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.rfqLoading = false)),
            map((resp) => resp.data),
          ),
        ),
      ),
    );
  }

  private initPickingWarehouseId() :Observable<any> {
    return this.inventorySettings$.pipe(
      tap((settings :any) => {
        if (settings && settings.id) {
          this.inventorySettings = JSON.parse(settings.value);
          this.inventorySettings.warehouse_id = parseInt(
            this.inventorySettings.warehouse_id,
          );
          this.warehouse_id = this.inventorySettings.warehouse_id;
          this.loadLocationFromWarehouse(
            this.inventorySettings.warehouse_id,
          ).subscribe();
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private initPickingLocationId() :Observable<any> {
    return this.locations$.pipe(
      tap((locations :PickingLocation[]) => {
        if (locations?.length > 0) {
          const receivingLocation = locations?.find(
            (location) =>
              location.location_name === DefaultPickingLocation.RECEIVING,
          );
          const shippingLocation = locations?.find(
            (location) =>
              location.location_name === DefaultPickingLocation.SHIPPING,
          );
          const pickingLocation = locations?.find(
            (location) =>
              location.is_pick_location === 1 &&
              !(
                location.location_name === DefaultPickingLocation.RECEIVING ||
                location.location_name === DefaultPickingLocation.SHIPPING
              ),
          );
          this.location_id =
            pickingLocation?.id ??
            receivingLocation?.id ??
            shippingLocation?.id ??
            0;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }
}
